import { useCallback, useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';

type ScrollDirection = 'back' | 'forward';

type UseSpaceWidgetSwiperOptions = {
  itemCount: number;
  hasMore?: boolean;
  loading?: boolean;
  onNearEnd?: () => void;
  resetKey?: string;
};

export const useSpaceWidgetSwiper = ({
  itemCount,
  hasMore = false,
  loading = false,
  onNearEnd,
  resetKey,
}: UseSpaceWidgetSwiperOptions) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const updateScrollState = useCallback((swiper: SwiperType) => {
    setCanScrollBack(!swiper.isBeginning);
    setCanScrollForward(!swiper.isEnd);
  }, []);

  const handleSwiper = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      updateScrollState(swiper);
    },
    [updateScrollState]
  );

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      updateScrollState(swiper);

      if (!onNearEnd || loading || !hasMore) {
        return;
      }

      const remainingSlides = itemCount - (swiper.activeIndex + 1);

      if (remainingSlides <= 3) {
        onNearEnd();
      }
    },
    [hasMore, itemCount, loading, onNearEnd, updateScrollState]
  );

  const handleReachEnd = useCallback(() => {
    const swiper = swiperRef.current;

    if (swiper) {
      updateScrollState(swiper);
    }

    if (!onNearEnd || loading || !hasMore) {
      return;
    }

    onNearEnd();
  }, [hasMore, loading, onNearEnd, updateScrollState]);

  const handleTransitionEnd = useCallback(
    (swiper: SwiperType) => {
      updateScrollState(swiper);
    },
    [updateScrollState]
  );

  const handleScroll = useCallback(
    (direction: ScrollDirection) => {
      const swiper = swiperRef.current;

      if (!swiper) {
        return;
      }

      if (direction === 'back') {
        if (!swiper.isBeginning) {
          swiper.slidePrev();
        }
        return;
      }

      if (swiper.isEnd) {
        if (hasMore && !loading) {
          onNearEnd?.();
        }
        return;
      }

      swiper.slideNext();
    },
    [hasMore, loading, onNearEnd]
  );

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper) {
      return;
    }

    swiper.slideTo(0, 0);
    swiper.update();
    updateScrollState(swiper);
  }, [resetKey, updateScrollState]);

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper) {
      return;
    }

    swiper.update();
    updateScrollState(swiper);
  }, [itemCount, updateScrollState]);

  return {
    handleSwiper,
    handleSlideChange,
    handleReachEnd,
    handleTransitionEnd,
    canScrollBack,
    canScrollForward,
    handleScroll,
  };
};
